import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Typography, InputNumber, App, Row, Col, Input, Alert, Select, Tooltip, Divider } from 'antd';
import { WifiOutlined, PlayCircleOutlined, ApiOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import apiClient from '../api/client';
import { CheckedBy, DiagResponse, DiagWorker, ProxyDiagResponse, ProxyEntry } from '../components/diagnostics/model';
import PingResultPanel from '../components/diagnostics/PingResultPanel';
import ProxyResultPanel from '../components/diagnostics/ProxyResultPanel';

const { Title, Text } = Typography;
const { TextArea } = Input;

const defaultTargets = '8.8.8.8, 1.1.1.1, 208.67.222.222, 9.9.9.9, 77.88.8.8';

const emptyProxy = (): ProxyEntry => ({ protocol: 'http', host: '', port: 8080, login: '', password: '' });

/** Field is the label above a control: 12px (the readable floor), muted, and the explanation that
 *  used to sit under it as a full paragraph now lives in its tooltip. */
const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div>
    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 2 }}>
      {hint ? <Tooltip title={hint}><span style={{ borderBottom: '1px dotted currentColor' }}>{label}</span></Tooltip> : label}
    </Text>
    {children}
  </div>
);

const DiagnosticsPage: React.FC = () => {
  const { message } = App.useApp();
  const [concurrency, setConcurrency] = useState<number>(20);
  const [pingCount, setPingCount] = useState<number>(3);
  const [customTargets, setCustomTargets] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DiagResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pingCheckedBy, setPingCheckedBy] = useState<CheckedBy | null>(null);

  // Proxy diagnostic state
  const [proxyList, setProxyList] = useState<ProxyEntry[]>([emptyProxy()]);
  const [proxyTargetURL, setProxyTargetURL] = useState('http://detectportal.firefox.com/success.txt');
  const [proxyMaxLatency, setProxyMaxLatency] = useState(30000);
  const [proxyLoading, setProxyLoading] = useState(false);
  const [proxyResult, setProxyResult] = useState<ProxyDiagResponse | null>(null);
  const [proxyError, setProxyError] = useState<string | null>(null);
  const [proxyCheckedBy, setProxyCheckedBy] = useState<CheckedBy | null>(null);

  // Worker selection: '' = auto (first available connected worker).
  const [workers, setWorkers] = useState<DiagWorker[]>([]);
  const [workerId, setWorkerId] = useState<string>('');

  useEffect(() => {
    apiClient.get('/admin/workers')
      .then(res => { if (res.data?.success) setWorkers(res.data.data || []); })
      .catch(() => { /* selection stays on Auto */ });
  }, []);

  const workerOptions = [
    { value: '', label: 'Auto — external worker preferred' },
    ...workers.map(w => ({
      value: w.id,
      label: `${w.name}${w.country ? ` (${w.country}${w.city ? `, ${w.city}` : ''})` : ''}${w.live?.online === false ? ' — offline' : ''}`,
      disabled: w.live?.online === false,
    })),
  ];

  const diagUrl = (path: string) => workerId ? `${path}?workerId=${encodeURIComponent(workerId)}` : path;

  const handleRun = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    setPingCheckedBy(null);
    try {
      const payload: any = {
        concurrency,
        count: pingCount,
      };

      // Parse custom targets if provided
      const rawTargets = customTargets.trim();
      if (rawTargets) {
        const targets = rawTargets
          .split(/[\n,;]+/)
          .map(t => t.trim())
          .filter(t => t.length > 0);
        if (targets.length > 0) {
          payload.targets = targets;
        }
      }

      const res = await apiClient.post(diagUrl('/health/diag/ping'), payload, { timeout: 60000 });
      if (res.data?.success) {
        // The envelope's data is a DiagResult wrapper: {success, data, error,
        // workerName, workerIP, workerCountry} — the payload lives one level in.
        const diag = res.data.data;
        if (diag?.success === false) {
          setError(diag.error || 'Worker reported a failure');
        } else {
          setResult(diag?.data ?? null);
          setPingCheckedBy(diag ?? null);
        }
      } else {
        setError(res.data?.error || 'Unknown error from server');
      }
    } catch (err: any) {
      const detail = err.response?.data?.error
        || err.response?.data?.message
        || err.message
        || 'Request failed';
      const status = err.response?.status ? ` (HTTP ${err.response.status})` : '';
      setError(`${detail}${status}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRunProxy = async () => {
    const validProxies = proxyList.filter(p => p.host.trim());
    if (validProxies.length === 0) {
      message.warning('Add at least one proxy with a host');
      return;
    }
    setProxyLoading(true);
    setProxyResult(null);
    setProxyError(null);
    setProxyCheckedBy(null);
    try {
      const res = await apiClient.post(diagUrl('/health/diag/proxy'), {
        proxies: validProxies.map(p => ({
          protocol: p.protocol,
          host: p.host.trim(),
          port: p.port,
          login: p.login || undefined,
          password: p.password || undefined,
        })),
        targetURL: proxyTargetURL,
        maxLatencyMs: proxyMaxLatency,
      }, { timeout: 120000 });
      if (res.data?.success) {
        const diag = res.data.data;
        if (diag?.success === false) {
          setProxyError(diag.error || 'Worker reported a failure');
        } else {
          setProxyResult(diag?.data ?? null);
          setProxyCheckedBy(diag ?? null);
        }
      } else {
        setProxyError(res.data?.error || 'Unknown error');
      }
    } catch (err: any) {
      const detail = err.response?.data?.error || err.message || 'Request failed';
      const status = err.response?.status ? ` (HTTP ${err.response.status})` : '';
      setProxyError(`${detail}${status}`);
    } finally {
      setProxyLoading(false);
    }
  };

  const updateProxy = (index: number, field: keyof ProxyEntry, value: any) => {
    setProxyList(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  const addProxy = () => {
    setProxyList(prev => [...prev, emptyProxy()]);
  };

  const removeProxy = (index: number) => {
    setProxyList(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div>
      <Title level={4} style={{ marginTop: 0, marginBottom: 10 }}>Diagnostics</Title>

      <Card
        title={<Space size={6}><WifiOutlined /><Text style={{ fontSize: 13 }}>ICMP ping test</Text></Space>}
        size="small"
        style={{ marginBottom: 12 }}
        styles={{ body: { padding: 12 } }}
      >
        <Text type="secondary" style={{ display: 'block', fontSize: 12, maxWidth: 860, marginBottom: 10 }}>
          Tests raw ICMP ping capability of the checker server, bypassing the application&apos;s internal ping limiter.
          Helps identify OS-level bottlenecks (ICMP socket limits, missing permissions, network issues).
        </Text>

        {/* One filter line, not four: the whole run configuration fits on a
            single row and wraps tightly on narrower windows. */}
        <Row gutter={[12, 8]} align="bottom">
          <Col>
            <Field label="Concurrent pings">
              <InputNumber
                size="small"
                value={concurrency}
                onChange={(val) => setConcurrency(val ?? 20)}
                min={1}
                max={100}
                step={5}
                style={{ width: 96 }}
              />
            </Field>
          </Col>
          <Col>
            <Field label="Pings per target">
              <InputNumber
                size="small"
                value={pingCount}
                onChange={(val) => setPingCount(val ?? 3)}
                min={1}
                max={10}
                step={1}
                style={{ width: 96 }}
              />
            </Field>
          </Col>
          <Col>
            <Field label="Run from worker">
              <Select
                size="small"
                value={workerId}
                onChange={setWorkerId}
                options={workerOptions}
                style={{ width: 240 }}
              />
            </Field>
          </Col>
          <Col flex="auto">
            <Field
              label="Custom targets (optional)"
              hint="IP addresses separated by commas, semicolons or new lines. Leave empty to use the default DNS servers (8.8.8.8, 1.1.1.1, …)."
            >
              <TextArea
                size="small"
                value={customTargets}
                onChange={(e) => setCustomTargets(e.target.value)}
                placeholder={defaultTargets}
                autoSize={{ minRows: 1, maxRows: 3 }}
                style={{ maxWidth: 420 }}
              />
            </Field>
          </Col>
          <Col>
            <Button
              size="small"
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleRun}
              loading={loading}
            >
              {loading ? 'Running…' : 'Run test'}
            </Button>
          </Col>
        </Row>
      </Card>

      {error && (
        <Alert
          message="Diagnostic failed"
          description={(
            <Space direction="vertical" size={2}>
              <Text style={{ fontSize: 13 }}>{error}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Check that the checker service is running and accessible from the API gateway.
                Verify docker-compose networking and that the checker container has NET_RAW capability.
              </Text>
            </Space>
          )}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      {result && <PingResultPanel result={result} checkedBy={pingCheckedBy} />}

      <Divider style={{ margin: '12px 0' }} />

      <Card
        title={<Space size={6}><ApiOutlined /><Text style={{ fontSize: 13 }}>Proxy connectivity test</Text></Space>}
        size="small"
        style={{ marginBottom: 12 }}
        styles={{ body: { padding: 12 } }}
      >
        <Text type="secondary" style={{ display: 'block', fontSize: 12, maxWidth: 860, marginBottom: 10 }}>
          Tests proxy connectivity with all 3 steps: TCP connect to proxy, HTTP request through proxy, latency evaluation.
          Bypasses the application&apos;s proxy rate limiter — a raw connectivity test.
        </Text>

        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          {proxyList.map((p, idx) => (
            <Row key={idx} gutter={6} align="middle">
              <Col>
                <Select
                  size="small"
                  value={p.protocol}
                  onChange={(val) => updateProxy(idx, 'protocol', val)}
                  aria-label={`Proxy ${idx + 1} protocol`}
                  style={{ width: 92 }}
                  options={[
                    { value: 'http', label: 'HTTP' },
                    { value: 'https', label: 'HTTPS' },
                    { value: 'socks5', label: 'SOCKS5' },
                  ]}
                />
              </Col>
              <Col flex="auto" style={{ maxWidth: 320 }}>
                <Input
                  size="small"
                  value={p.host}
                  onChange={(e) => updateProxy(idx, 'host', e.target.value)}
                  placeholder="Proxy host (IP or hostname)"
                  aria-label={`Proxy ${idx + 1} host`}
                />
              </Col>
              <Col>
                <InputNumber
                  size="small"
                  value={p.port}
                  onChange={(val) => updateProxy(idx, 'port', val ?? 8080)}
                  min={1}
                  max={65535}
                  style={{ width: 84 }}
                  placeholder="Port"
                  aria-label={`Proxy ${idx + 1} port`}
                />
              </Col>
              <Col>
                <Input
                  size="small"
                  value={p.login}
                  onChange={(e) => updateProxy(idx, 'login', e.target.value)}
                  placeholder="Login"
                  aria-label={`Proxy ${idx + 1} login`}
                  style={{ width: 110 }}
                />
              </Col>
              <Col>
                <Input.Password
                  size="small"
                  value={p.password}
                  onChange={(e) => updateProxy(idx, 'password', e.target.value)}
                  placeholder="Password"
                  aria-label={`Proxy ${idx + 1} password`}
                  style={{ width: 110 }}
                />
              </Col>
              <Col>
                {proxyList.length > 1 && (
                  <Tooltip title="Remove proxy">
                    <Button
                      size="small"
                      icon={<MinusCircleOutlined />}
                      onClick={() => removeProxy(idx)}
                      aria-label={`Remove proxy ${idx + 1}`}
                      type="text"
                      danger
                    />
                  </Tooltip>
                )}
              </Col>
            </Row>
          ))}

          <Button size="small" type="dashed" onClick={addProxy} icon={<PlusOutlined />} style={{ width: 150 }}>
            Add proxy
          </Button>

          <Row gutter={[12, 8]} align="bottom">
            <Col>
              <Field label="Target URL">
                <Input
                  size="small"
                  value={proxyTargetURL}
                  onChange={(e) => setProxyTargetURL(e.target.value)}
                  placeholder="http://detectportal.firefox.com/success.txt"
                  style={{ width: 280 }}
                />
              </Field>
            </Col>
            <Col>
              <Field label="Max latency (ms)">
                <InputNumber
                  size="small"
                  value={proxyMaxLatency}
                  onChange={(val) => setProxyMaxLatency(val ?? 30000)}
                  min={100}
                  max={120000}
                  step={1000}
                  style={{ width: 110 }}
                />
              </Field>
            </Col>
            <Col>
              <Field label="Run from worker">
                <Select
                  size="small"
                  value={workerId}
                  onChange={setWorkerId}
                  options={workerOptions}
                  style={{ width: 240 }}
                />
              </Field>
            </Col>
            <Col>
              <Button
                size="small"
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleRunProxy}
                loading={proxyLoading}
              >
                {proxyLoading ? 'Testing…' : 'Run proxy test'}
              </Button>
            </Col>
          </Row>
        </Space>
      </Card>

      {proxyError && (
        <Alert
          message="Proxy diagnostic failed"
          description={<Text style={{ fontSize: 13 }}>{proxyError}</Text>}
          type="error"
          showIcon
          closable
          onClose={() => setProxyError(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      {proxyResult && <ProxyResultPanel result={proxyResult} checkedBy={proxyCheckedBy} />}
    </div>
  );
};

export default DiagnosticsPage;
